import { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { makeStyles } from "@material-ui/core/styles"

import FormControl from "@material-ui/core/FormControl"
import TextField from "@material-ui/core/TextField"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import Checkbox from "@material-ui/core/Checkbox"
import InputLabel from "@material-ui/core/InputLabel"
import Select from "@material-ui/core/Select"
import FormHelperText from "@material-ui/core/FormHelperText"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"

//import PermMediaIcon from "@material-ui/icons/PermMedia"
//import InputAdornment from "@material-ui/core/InputAdornment"
import AvatarInput from "components/partials/AvatarInput"

import { useIsInputWritable } from "common/hooks"
import { Role } from "common/types/user.type"
import { MenuItem } from "components/partials"
import { CompaniesApi } from "common/api"

const useStyles = makeStyles(theme => ({
    formControl: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(4),
        alignItems: "center",

        // Adjustment to align checkbox with text input
        "& .MuiFormControlLabel-root": {
            position: "relative",
            top: 8
        }
    },
    checkbox: {
        marginRight: 0,
        "& .MuiFormControlLabel-label": {
            flexGrow: 1
        }
    }
}))

const Form = props => {
    const { formData, error, onFormChange, formProps } = props
    const { t } = useTranslation()
    const classes = useStyles()
    const isWritable = useIsInputWritable()

    const [companies, setCompanies] = useState(null)
    const { current: initialCompany } = useRef(formData.company)
    const isCreatingCatalog = formData.id === undefined
    const isEnabled = Boolean(formData.enabled)

    console.log('formData:', formData)

    useEffect(() => {
        CompaniesApi.get().then(response => {
            setCompanies(response.data)
        })
    }, [])

    const onChange = key => event => {
        let { value, checked } = event.target

        if (event.target.type === "checkbox") {
            onFormChange(key, checked)
            return
        }

        if (hasCompanies && key === "company") {
            onFormChange(
                key,
                companies.find(({ id }) => id === value)
            )
            return
        }

        onFormChange(key, value)
    }

    function onChangeProfile(base64Image) {
        onFormChange("profileImage", base64Image)
    }

    const hasCompanies = Array.isArray(companies)

    return (
        <form {...formProps}>
            <FormControl fullWidth style={{ display: "flex", alignItems: "center" }}>
                <AvatarInput
                    onChange={onChangeProfile}
                    AvatarProps={{
                        src: formData?.profileImage || undefined
                    }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal" className={classes.formControl}>
                <TextField
                    required
                    label={t("view.adminCatalogs.catalogIdLabel")}
                    value={formData.name || ""}
                    onChange={onChange("name")}
                    error={Boolean(error?.name)}
                    helperText={error?.name}
                    disabled={!isWritable(Role.Admin)}
                    InputProps={{
                        readOnly: !isWritable(Role.Admin),
                    }}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            name="enabled"
                            color="primary"
                            checked={Boolean(formData.enabled)}
                            onChange={onChange("enabled")}
                            disabled={!isWritable(Role.Moderator)}
                        />
                    }
                    label={t("view.adminCatalogs.catalogEnabledLabel")}
                />
            </FormControl>

            <FormControl margin="normal" className={classes.formControl} >
                <TextField
                    required
                    label={t("view.adminCatalogs.titleLabel")}
                    value={formData.title || ""}
                    onChange={onChange("title")}
                    error={Boolean(error?.title)}
                    helperText={error?.title}
                    disabled={!isWritable(Role.Admin)}
                    InputProps={{
                        readOnly: !isWritable(Role.Admin),
                    }}
                />

                <TextField
                    label={t("view.adminCatalogs.bgColor")}
                    value={formData.bgColor || ""}
                    onChange={onChange("bgColor")}
                    error={Boolean(error?.bgColor)}
                    helperText={error?.bgColor}
                    disabled={!isWritable(Role.Admin)}
                    InputProps={{
                        readOnly: !isWritable(Role.Admin),
                    }}
                />
            </FormControl>

            <FormControl margin="normal" className={classes.formControl}>
                <TextField
                    required
                    label={t("view.adminCatalogs.subTitleLabel")}
                    value={formData.subTitle || ""}
                    onChange={onChange("subTitle")}
                    error={Boolean(error?.subTitle)}
                    helperText={error?.subTitle}
                    disabled={!isWritable(Role.Admin)}
                    InputProps={{
                        readOnly: !isWritable(Role.Admin),
                    }}
                />

            </FormControl>

            <FormControl
                fullWidth
                margin="normal"
                style={{
                    display: isWritable(Role.Superadmin) ? "inline-flex" : "none"
                }}
            >
                <InputLabel {...(!formData.company && { shrink: true })}>
                    {t("view.admin.company")}
                </InputLabel>
                <Select
                    displayEmpty
                    value={hasCompanies && formData.company ? formData.company.id : ""}
                    onChange={onChange("company")}
                    error={Boolean(error?.company)}
                    disabled={!isWritable(Role.Superadmin)}
                    {...(hasCompanies && {
                        renderValue: selected => {
                            if (selected === "") return t("view.adminCatalogs.noCompany")
                            const company = companies.find(({ id }) => selected === id)
                            return <CompanyMenuItem company={company} />
                        }
                    })}
                >
                    <MenuItem value="">{t("view.adminCatalogs.noCompany")}</MenuItem>
                    {hasCompanies &&
                        companies.map(company => {
                            const isInitialCompany = initialCompany?.id === company.id

                            // Make sure to allow admin to select catalog's initial company, whether
                            // that company has available licenses or not.
                            let disabled = getCompanyAvailableLicenses(company) <= 0
                            if (
                                (!isCreatingCatalog && isInitialCompany) ||
                                company.unlimitedCatalogLicenses
                            ) {
                                disabled = false
                            }

                            // Disabled catalog does not count affect licenses.
                            if (!isEnabled) {
                                disabled = false
                            }

                            return (
                                <MenuItem
                                    key={company.id}
                                    value={company.id}
                                    disabled={disabled}
                                >
                                    <CompanyMenuItem company={company} />
                                </MenuItem>
                            )
                        })}
                </Select>
                <FormHelperText error>{error?.company}</FormHelperText>
            </FormControl>
        </form>
    )
}

const CompanyMenuItem = props => {
    const { t } = useTranslation()
    const { company } = props
    const { name, catalogLicenses, unlimitedCatalogLicenses } = company
    const availableLicenses = getCompanyAvailableLicenses(company)

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
        >
            <Typography component="span">{name}</Typography>
            <Typography component="span" variant="body2" color="textSecondary">
                {unlimitedCatalogLicenses
                    ? t("view.adminCatalogs.unlimitedLicenses")
                    : t("view.adminCatalogs.totalUsersOverLicenses", {
                        total: availableLicenses,
                        licenses: catalogLicenses
                    })}
            </Typography>
        </Box>
    )
}

export function getCompanyAvailableLicenses(company) {
    if (company.unlimitedCatalogLicenses) {
        return Infinity
    }

    return company.catalogLicenses - company.totalOwnedCatalogs
}

export default Form
